import { tr } from './tr';
import { en } from './en';

/**
 * Translation, done in the client.
 *
 * <p>Not on the server, and the reason is the audit flow: a device in a stockroom with no
 * signal still has to tell the auditor why their answer will not submit. If the sentence had
 * to be fetched, it would not be there when it is needed. So the server sends a stable code
 * and the client holds the table.</p>
 *
 * <p>No library. Turkish and English with simple interpolation is about forty lines; i18next
 * is forty kilobytes on a phone that is already waiting on a photograph upload. Plurals and
 * number formatting go through the platform's own `Intl` if they are ever needed.</p>
 */
export type Language = 'tr' | 'en';

export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
];

const DICTIONARIES: Record<Language, Record<string, string>> = { tr, en };

const STORAGE_KEY = 'denetim.language';

function detect(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'tr' || stored === 'en') return stored;
  } catch {
    // Storage can be blocked. The browser's own preference is a fine second answer.
  }

  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('tr')
    ? 'tr'
    : 'en';
}

let current: Language = detect();
const listeners = new Set<() => void>();

export function language(): Language {
  return current;
}

export function setLanguage(next: Language) {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Remembering the choice is a convenience; not remembering it is not a failure.
  }
  // Kept in step because CSS text-transform and toLocaleUpperCase both read it: without
  // it, "Tanımlar" upper-cases to "TANIMLAR" but "İş" becomes "IŞ", which is a misspelling.
  // Guarded because this module is also exercised outside a browser, in its own tests.
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  for (const listener of listeners) listener();
}

export function onLanguageChange(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Looks a key up, filling in `{name}` placeholders.
 *
 * <p>A missing key falls back to English and then to the key itself. Showing the key is ugly
 * and deliberate: a blank where a sentence should be looks like the product working, and
 * this looks like what it is.</p>
 */
export function t(key: string, args?: Record<string, string | number>): string {
  const template = DICTIONARIES[current][key] ?? DICTIONARIES.en[key] ?? key;
  if (!args) return template;

  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in args ? String(args[name]) : whole,
  );
}

/**
 * Turns a server refusal into a sentence.
 *
 * <p>The server sends a code and an English message. A code this build has a translation for
 * becomes the reader's own language; one it does not — a newer server, an app that has not
 * been updated yet — falls back to the server's own words. Something true beats nothing.</p>
 */
export function translateError(code: string | null, fallback: string, args?: Record<string, string>): string {
  if (!code) return fallback;

  const key = `error.${code}`;
  const dictionary = DICTIONARIES[current];
  if (key in dictionary || key in DICTIONARIES.en) return t(key, args);

  return fallback;
}

/** BCP-47 tags, for the Intl objects below and for locale-aware casing. */
const TAGS: Record<Language, string> = { tr: 'tr-TR', en: 'en-GB' };

/**
 * Intl objects are not cheap to build and the language changes about twice a year, so they
 * are built once per language and thrown away when it changes.
 */
let intl: { for: Language; number: Intl.NumberFormat; plural: Intl.PluralRules; collator: Intl.Collator } | null = null;

function tools() {
  if (intl?.for !== current) {
    const tag = TAGS[current];
    intl = {
      for: current,
      number: new Intl.NumberFormat(tag),
      plural: new Intl.PluralRules(tag),
      collator: new Intl.Collator(tag, { sensitivity: 'base', numeric: true }),
    };
  }

  return intl;
}

/**
 * Turns a server code into a sentence, or into itself.
 *
 * <p>The fallback is the point. An offline device runs the build it was given, and the
 * server it syncs with may be newer — a question type added last week arrives as a code this
 * bundle has never seen. Showing `MultipleChoice` is not pretty, but it is true, and it is
 * what an auditor can read out over the phone. A blank cell looks like the product working.</p>
 */
export function tCode(namespace: string, code: string): string {
  const key = `${namespace}.${code}`;
  return DICTIONARIES[current][key] ?? DICTIONARIES.en[key] ?? code;
}

/** Whether a code has a translation, for the things that are only shown when they exist. */
export function hasCode(namespace: string, code: string): boolean {
  const key = `${namespace}.${code}`;
  return key in DICTIONARIES[current] || key in DICTIONARIES.en;
}

/**
 * Plural-aware lookup: `{key}.one` or `{key}.other`, chosen by the platform's own rules.
 *
 * <p>Turkish takes the singular after a number ("3 şablon") and English does not
 * ("3 templates"), which is exactly the kind of thing Intl.PluralRules already knows and we
 * would otherwise get wrong in one of the two languages.</p>
 */
export function tn(key: string, count: number, args?: Record<string, string | number>): string {
  const category = tools().plural.select(count);
  const dictionary = DICTIONARIES[current];
  const chosen = `${key}.${category}` in dictionary ? `${key}.${category}` : `${key}.other`;

  return t(chosen, { ...args, count });
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return options ? new Intl.NumberFormat(TAGS[current], options).format(value) : tools().number.format(value);
}

/**
 * A number with a fixed count of decimals, in the reader's language: 94,74 in Turkish, not
 * the 94.74 that toFixed gives everybody (field test, 2 November).
 */
export function fixed(value: number, digits: number): string {
  return formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatDate(value: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(TAGS[current], options ?? { dateStyle: 'medium' }).format(date);
}

/**
 * Upper-casing that knows which language it is in.
 *
 * <p>`'i'.toUpperCase()` is `'I'`, which in Turkish is a different letter: "Tanımlar" would
 * become "TANIMLAR" correctly but "İş" would become "IŞ", which is a misspelling. Never use
 * the plain method on anything a reader will see.</p>
 */
export function upper(text: string): string {
  return text.toLocaleUpperCase(TAGS[current]);
}

/**
 * Upper-casing for codes a machine reads — category prefixes, question codes.
 *
 * <p>The opposite rule to {@link upper}: the server folds codes with ToUpperInvariant and
 * accepts ASCII letters only, so "vit" must become "VIT". Turkish casing makes it "VİT",
 * which the server rejects as a non-letter and which can never match the existing VIT.</p>
 */
export function codeUpper(text: string): string {
  return text.toUpperCase();
}

/**
 * The longest code the server keeps (Domain CodeLength.Max). Held at the box, so a code that
 * is too long cannot be typed rather than being refused at Save (field test, 3 November).
 */
export const CODE_MAX = 50;

/** Sorting that puts ç, ğ, ı, ö, ş and ü where a Turkish reader looks for them. */
export function compare(a: string, b: string): number {
  return tools().collator.compare(a, b);
}

/**
 * Lower-cases, strips accents and folds dotless ı onto i.
 *
 * <p>So that searching is forgiving of the keyboard someone is holding: "sogutma" finds
 * "Soğutma", "is" finds "İş". Typing the right diacritics should never be the difference
 * between finding a question and believing it does not exist.</p>
 */
export function fold(text: string): string {
  return text
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i');
}

/** Case- and accent-insensitive "contains", for client-side filtering. */
export function matches(haystack: string, needle: string): boolean {
  const wanted = fold(needle.trim());
  return wanted.length === 0 || fold(haystack).includes(wanted);
}
